import type { ActiveModuleName } from '@/modules/companies/CompanySettings/hooks'
import {
  useCompositeModules,
  useModuleConfigs,
} from '@/modules/companies/CompanySettings/hooks'
import { writeAuthCookie } from '@/modules/login/util'
import type { ResultNode } from '@/types/util'
import { FLEET_URL, PLANNER_URL } from '@/utils/env'
import type { SidebarGroupId } from '@/utils/hooks'
import { useAdmin, useModuleConfig, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import type { LucideIcon } from 'lucide-react'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO, startOfMonth, subDays, subMonths } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { MODULE_ICONS } from '../common/module-icons'
import { Module, ModuleOptionsValue } from '../companies/consts'
import { SIDEBAR_QUERY } from './queries'

type ModuleName = ActiveModuleName | 'main'

type FormTemplate = ResultNode<typeof SIDEBAR_QUERY, 'allFormTemplates'>

export interface ItemGroup {
  type: 'group'
  id: SidebarGroupId
  header: string
  tall?: boolean

  module?: ModuleName
  permissions?: string | string[]
  allowAdmin?: boolean
  hide?: boolean

  items: SidebarItem[]
}

export interface SidebarItem {
  type: 'item'
  to: string
  text: string
  end?: true

  className?: string
  icon: LucideIcon
  badge?: string | number
  badgeLoading?: boolean

  target?: '_blank'

  module?: ModuleName
  permissions?: string | string[]
  allowAdmin?: boolean
  hide?: boolean

  onBeforeNavigate?(): void
}

export type SidebarConfig = (ItemGroup | SidebarItem)[]

export function useSidebarConfig(): SidebarConfig {
  const translations = useTranslate({
    labelGeneral: 'common.general',
    labelDocumentation: 'common.documentation',
    labelInformation: 'common.information',
    forms: 'common.forms',
    drivingWithoutCard: 'activities.driving-without-card',
    drivingInOut: 'activities.driving-in-out',

    remainingDrivingTime: 'common.remaining-driving-time',

    menu: 'common.menu',

    dashboard: 'common.dashboard',
    learning: 'learning.title',
    users: 'common.users',
    userDocuments: 'common.user-documents',
    forum: 'common.forum',
    drivingData: 'common.driving-data',
    issues: 'common.issues',
    infringements: 'common.infringements',
    infringementAnalytics: 'infringements.analytics.menu',
    driverGrade: 'driver-grade.title',
    timeEntries: 'common.hourlist',
    absences: 'common.absence',
    handbook: 'common.driversHandbook',
    formCreator: 'common.formcreator',
    companySettings: 'settings.company-settings',
    vehicleFleet: 'vehicles.fleet-overview',
    vehicleDocuments: 'vehicles.vehicle-documents',
    vehicleExpirations: 'vehicles.expiration-dates',
    vehicleServices: 'vehicles.vehicle-services',
    fleet: 'vehicles.fleet',
    driverPlanner: 'common.driver-planner',
    fleetManagement: 'vehicles.fleet-management',
    routePlanner: 'common.route-planner',
    exports: 'common.exports-settings',
    userExports: 'exports.user-exports',
    salaryBases: 'salary.salary-bases',
    workTimeAndSalary: 'salary.work-time-and-salary',
    checklists: 'common.checklists',
    exportAndSettings: 'common.export-and-settings',
    confirmHours: 'salary.confirm-hours',
    statusActivities: 'activities.activities-status',
    fileArchive: 'activities.file-archive',
    analytics: 'common.analytics',
  })

  const admin = useAdmin()

  const { active } = useModuleConfigs()
  const activityModule = useModuleConfig(Module.ACTIVITIES)
  const salaryModule = useModuleConfig(Module.SALARY)
  const { mainModuleActive } = useCompositeModules()
  const { hasPermissions } = usePermissions()

  // Each counter is asked for only when the requester may read it: these
  // fields raise a permission error server-side, and a single errored non-null
  // field nulls the whole response — taking every other badge with it.
  const canViewIssues = hasPermissions(PERMISSIONS.issues.view.issue)
  const canViewAbsences = hasPermissions(PERMISSIONS.timesheets.view.absence)
  const canViewInfringements = hasPermissions(
    PERMISSIONS.infringements.view.infringement
  )
  const canOpenExports =
    (mainModuleActive &&
      hasPermissions(PERMISSIONS.companies.export.exportReports)) ||
    (active.absences &&
      hasPermissions(PERMISSIONS.timesheets.view.othersabsence))

  const { data, loading } = useQuery(SIDEBAR_QUERY, {
    variables: {
      workPeriodStartDate: formatISO(startOfMonth(subDays(new Date(), 14))),
      filterEndDate: formatISO(new Date()),
      lastYear: formatISO(subMonths(new Date(), 12)),
      canViewIssues,
      canViewAbsences,
      canViewInfringements,
    },
  })

  const issueCount = data?.allIssues?.totalUnresolvedCount
  const absenceCount = data?.absenceCountByStatus
  const infringementCount = data?.unresolvedInfringementCount
  const confirmSalaryCount = data?.workPeriodApprovalCount

  const showFileUpload =
    (activityModule.getModuleOptionStatus(
      ModuleOptionsValue.ACTIVITIES_READ_TACHO_FILES_TO_ACTIVITIES
    ) &&
      activityModule.getModuleOptionStatus(
        ModuleOptionsValue.ACTIVITIES_PROCESS_TACHO_FILES
      )) ||
    admin

  const formTemplates = useMemo(
    () =>
      data?.allFormTemplates.edges.reduce<FormTemplate[]>(
        (acc, edge) => (edge?.node ? [...acc, edge.node] : acc),
        []
      ) ?? [],
    [data?.allFormTemplates.edges]
  )

  const canSeeOthersWorkPeriods = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )

  const canApproveOwnHours =
    salaryModule.getModuleOptionStatus(
      ModuleOptionsValue.SALARY_USER_APPROVE_HOURS
    ) || hasPermissions(PERMISSIONS.salary.approve.workperiod)

  const canApproveOthersHours =
    salaryModule.getModuleOptionStatus(
      ModuleOptionsValue.SALARY_LEADER_APPROVE_HOURS
    ) && canSeeOthersWorkPeriods

  // The badge counts periods waiting for an approval step — the leader
  // approval for someone who sees others' periods, the driver's own
  // confirmation for everyone else (matching how the backend scopes the
  // count). With that step turned off nothing is waiting, so the badge goes
  // instead of showing a number nobody can bring down.
  const approvalStepActive = salaryModule.getModuleOptionStatus(
    canSeeOthersWorkPeriods
      ? ModuleOptionsValue.SALARY_LEADER_APPROVE_HOURS
      : ModuleOptionsValue.SALARY_USER_APPROVE_HOURS
  )

  const canTransferWorkToAbsence = salaryModule.getModuleOptionStatus(
    ModuleOptionsValue.SALARY_ALLOW_TRANSFER_OF_HOURS_TO_ABSENCES
  )

  const fullConfig = useMemo<SidebarConfig>(
    () => [
      {
        type: 'item',
        to: '/dashboard',
        text: translations.dashboard,
        icon: MODULE_ICONS.dashboard,
      },
      {
        type: 'group',
        id: 'general',
        header: translations.labelGeneral,
        tall: isMobile,
        items: [
          {
            type: 'item',
            to: '/users',
            text: translations.users,
            end: true,
            icon: MODULE_ICONS.users,
            permissions: PERMISSIONS.users.view.user,
          },
          {
            type: 'item',
            to: '/user-documents',
            text: translations.userDocuments,
            className: 'user-documents',
            icon: MODULE_ICONS.userDocuments,
            // The document glyph is portrait, so at the default 28px width it
            // draws half again as tall as the square icons around it. Drawing
            // it smaller inside a full-width slot keeps both the icon and the
            // label in line with them.
            permissions: [
              PERMISSIONS.users.view.user,
              PERMISSIONS.users.view.userdocument,
              PERMISSIONS.users.view.othersuserdocument,
            ],
            allowAdmin: true,
          },
          {
            type: 'item',
            to: '/forum',
            text: translations.forum,
            icon: MODULE_ICONS.forum,
            module: 'forum',
            permissions: PERMISSIONS.forum.view.thread,
          },
        ],
      },
      {
        type: 'group',
        id: 'workTimeAndSalary',
        header: translations.workTimeAndSalary,
        module: 'salary',
        items: [
          {
            type: 'item',
            to: '/work-periods',
            text: translations.workTimeAndSalary,
            icon: MODULE_ICONS.workTimeAndSalary,
            module: 'salary',
            permissions: PERMISSIONS.salary.view.salarybase,
          },
          {
            type: 'item',
            to: '/approve-work-periods',
            text: translations.confirmHours,
            badge: approvalStepActive ? confirmSalaryCount : undefined,
            icon: MODULE_ICONS.confirmHours,
            module: 'salary',
            hide:
              !canApproveOwnHours &&
              !canApproveOthersHours &&
              !canTransferWorkToAbsence,
            permissions: PERMISSIONS.salary.approve.workperiod,
          },
        ],
      },
      {
        type: 'group',
        id: 'documentation',
        header: translations.labelDocumentation,
        module: 'main',
        items: [
          {
            type: 'item',
            to: '/activities',
            text: translations.drivingData,
            icon: MODULE_ICONS.drivingData,
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
          {
            type: 'item',
            to: '/issues',
            text: translations.issues,
            icon: MODULE_ICONS.issues,
            badge: issueCount,
            badgeLoading: loading,
            module: 'issues',
            permissions: PERMISSIONS.issues.view.issue,
          },
          {
            type: 'item',
            to: '/infringements',
            text: translations.infringements,
            badge: infringementCount,
            icon: MODULE_ICONS.infringements,
            module: 'infringements',
            permissions: PERMISSIONS.infringements.view.infringement,
          },
          {
            type: 'item',
            to: '/time-entries',
            text: translations.timeEntries,
            className: 'time-entries',
            icon: MODULE_ICONS.timeEntries,
            module: 'timesheets',
            permissions: PERMISSIONS.timesheets.view.timeentry,
          },
          {
            type: 'item',
            to: '/absences',
            text: translations.absences,
            icon: MODULE_ICONS.absences,
            badge: absenceCount,
            badgeLoading: loading,
            module: 'absences',
            permissions: [
              PERMISSIONS.timesheets.view.absence,
              PERMISSIONS.users.view.user,
            ],
          },
        ],
      },
      {
        type: 'group',
        id: 'fleet',
        header: translations.fleet,
        module: 'vehicles',
        items: [
          {
            type: 'item',
            to: '/vehicle-fleet',
            text: translations.vehicleFleet,
            className: 'vehicle-fleet',
            icon: MODULE_ICONS.vehicleFleet,
            permissions: PERMISSIONS.vehicles.view.vehicle,
          },
          {
            type: 'item',
            to: '/vehicle-documents',
            text: translations.vehicleDocuments,
            className: 'vehicle-documents',
            icon: MODULE_ICONS.vehicleDocuments,
            permissions: [
              PERMISSIONS.vehicles.view.vehicle,
              PERMISSIONS.vehicles.view.vehicledocument,
            ],
            allowAdmin: true,
          },
          {
            type: 'item',
            to: '/vehicle-expirations',
            text: translations.vehicleExpirations,
            className: 'vehicle-expirations',
            icon: MODULE_ICONS.vehicleExpirations,
            permissions: [
              PERMISSIONS.vehicles.view.vehicle,
              PERMISSIONS.vehicles.view.vehicleexpiration,
            ],
            allowAdmin: true,
          },
          {
            type: 'item',
            to: '/vehicle-services',
            text: translations.vehicleServices,
            className: 'vehicle-services',
            icon: MODULE_ICONS.vehicleServices,
            permissions: [
              PERMISSIONS.vehicles.view.vehicle,
              PERMISSIONS.vehicles.view.vehicleexpiration,
            ],
            allowAdmin: true,
          },
          {
            type: 'item',
            to: FLEET_URL,
            text: translations.fleetManagement,
            className: 'fleet',
            icon: MODULE_ICONS.fleetManagement,
            target: '_blank',
            module: 'fleet',
            permissions: PERMISSIONS.fleet.view.vehiclegroup,
            onBeforeNavigate: () => writeAuthCookie(),
          },
          {
            type: 'item',
            to: PLANNER_URL,
            text: translations.driverPlanner,
            icon: MODULE_ICONS.driverPlanner,
            target: '_blank',
            module: 'driverPlanner',
            permissions: PERMISSIONS.planner.view.calendaritem,
            onBeforeNavigate: () => writeAuthCookie(),
          },
          {
            type: 'item',
            to: '/remaining-driving',
            text: translations.remainingDrivingTime,
            icon: MODULE_ICONS.remainingDrivingTime,
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
        ],
      },
      {
        type: 'group',
        id: 'analytics',
        header: translations.analytics,
        module: 'activities',
        hide: !showFileUpload,
        permissions: PERMISSIONS.activities.change.driveractivity,
        items: [
          {
            type: 'item',
            to: '/analytics/infringements',
            text: translations.infringementAnalytics,
            icon: MODULE_ICONS.infringementAnalytics,
            module: 'infringements',
            permissions: PERMISSIONS.infringements.view.infringement,
            hide: !admin,
          },
          {
            type: 'item',
            to: '/analytics/driver-grade',
            text: translations.driverGrade,
            icon: MODULE_ICONS.driverGrade,
            module: 'driverGrade',
            hide: !admin,
          },
          {
            type: 'item',
            to: '/analytics/file-archive',
            text: translations.fileArchive,
            icon: MODULE_ICONS.fileArchive,
            module: 'activities',
            hide: !showFileUpload,
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/analytics/status',
            text: translations.statusActivities,
            icon: MODULE_ICONS.activitiesStatus,
            module: 'activities',
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/analytics/out-of-scope-driving',
            text: translations.drivingInOut,
            icon: MODULE_ICONS.drivingInOut,
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
          {
            type: 'item',
            to: '/analytics/driving-without-card',
            text: translations.drivingWithoutCard,
            icon: MODULE_ICONS.drivingWithoutCard,
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
        ],
      },
      {
        type: 'group',
        id: 'forms',
        header: translations.forms,
        permissions: PERMISSIONS.formcreator.view.formtemplate,
        items: formTemplates.map<SidebarItem>(node => ({
          type: 'item',
          to: `/formcreator/${node.id}`,
          text: node.name,
          icon: MODULE_ICONS.form,
          permissions: PERMISSIONS.formcreator.view.formtemplate,
        })),
      },
      {
        type: 'group',
        id: 'information',
        header: translations.labelInformation,
        items: [
          {
            type: 'item',
            to: '/handbook',
            text: translations.handbook,
            icon: MODULE_ICONS.handbook,
            module: 'handbook',
            permissions: PERMISSIONS.handbook.view.manualfolder,
          },
          {
            type: 'item',
            to: '/formcreator',
            end: true,
            text: translations.formCreator,
            icon: MODULE_ICONS.formCreator,
            module: 'handbook',
            permissions: PERMISSIONS.formcreator.view.formtemplate,
          },
          {
            type: 'item',
            to: '/checklists',
            end: true,
            text: translations.checklists,
            icon: MODULE_ICONS.checklists,
            module: 'handbook',
            permissions: PERMISSIONS.formcreator.change.filledoutform,
          },
        ],
      },
      {
        type: 'group',
        id: 'routePlanner',
        header: translations.routePlanner,
        module: 'routePlanner',
        hide: !admin,
        items: [
          {
            type: 'item',
            to: '/route-planner',
            text: translations.routePlanner,
            icon: MODULE_ICONS.routePlanner,
            permissions: PERMISSIONS.companies.view.companysetting,
          },
        ],
      },
      {
        type: 'group',
        id: 'exportAndSettings',
        header: translations.exportAndSettings,
        items: [
          {
            type: 'item',
            to: '/exports',
            text: translations.exports,
            className: 'exports',
            icon: MODULE_ICONS.exports,
            hide: !canOpenExports,
          },
          {
            type: 'item',
            to: '/user-exports',
            text: translations.userExports,
            className: 'user-exports',
            icon: MODULE_ICONS.userExports,
            permissions: PERMISSIONS.companies.export.exportReports,
          },
          {
            type: 'item',
            to: '/salary-bases',
            text: translations.salaryBases,
            icon: MODULE_ICONS.salaryBases,
            module: 'salary',
            permissions: PERMISSIONS.salary.change.salarybase,
          },
          {
            type: 'item',
            to: '/settings',
            text: translations.companySettings,
            className: 'settings',
            icon: MODULE_ICONS.companySettings,
            permissions: PERMISSIONS.companies.view.companysetting,
          },
        ],
      },
      {
        type: 'item',
        to: '/learning',
        text: translations.learning,
        icon: MODULE_ICONS.learning,
        module: 'learning',
      },
    ],
    [
      translations.dashboard,
      translations.learning,
      translations.labelGeneral,
      translations.users,
      translations.userDocuments,
      translations.forum,
      translations.workTimeAndSalary,
      translations.confirmHours,
      translations.labelDocumentation,
      translations.drivingData,
      translations.issues,
      translations.infringements,
      translations.timeEntries,
      translations.absences,
      translations.fleet,
      translations.vehicleFleet,
      translations.vehicleDocuments,
      translations.vehicleExpirations,
      translations.vehicleServices,
      translations.fleetManagement,
      translations.driverPlanner,
      translations.remainingDrivingTime,
      translations.analytics,
      translations.infringementAnalytics,
      translations.driverGrade,
      translations.fileArchive,
      translations.statusActivities,
      translations.drivingWithoutCard,
      translations.drivingInOut,
      translations.forms,
      translations.labelInformation,
      translations.handbook,
      translations.formCreator,
      translations.checklists,
      translations.routePlanner,
      translations.exportAndSettings,
      translations.exports,
      translations.userExports,
      canOpenExports,
      translations.salaryBases,
      translations.companySettings,
      confirmSalaryCount,
      approvalStepActive,
      canApproveOwnHours,
      canApproveOthersHours,
      canTransferWorkToAbsence,
      admin,
      issueCount,
      loading,
      infringementCount,
      absenceCount,
      showFileUpload,
      formTemplates,
    ]
  )

  const shouldShow = useCallback(
    (item: SidebarConfig[number]): boolean => {
      if ('hide' in item && item.hide) return false

      if (item.module) {
        if (item.module === 'main') return mainModuleActive
        if (!active[item.module]) return false
      }

      if ('permissions' in item) {
        if (!item.permissions) return true
        return hasPermissions(item.permissions, item.allowAdmin)
      }

      return true
    },
    [active, hasPermissions, mainModuleActive]
  )

  const finalConfig = useMemo<SidebarConfig>(() => {
    const config: SidebarConfig = []

    for (const item of fullConfig) {
      if (!shouldShow(item)) continue

      if (item.type === 'item') {
        config.push(item)
      } else {
        const group: ItemGroup = { ...item, items: [] }

        for (const subItem of item.items) {
          if (!shouldShow(subItem)) continue
          group.items.push(subItem)
        }

        if (group.items.length > 0) config.push(group)
      }
    }

    return config
  }, [fullConfig, shouldShow])

  return finalConfig
}
